import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useRefillsStore } from '../../../stores/useRefillsStore';
import { formatNumber, formatDate } from '../../../utils/formatters';
import { getIsImpersonating } from '../../../utils/session';
import type { Refill } from '../../../stores/types';

export interface ConfirmOrderPanelProps {
  view: 'List' | 'Grid';
  customerServicePhone: string;
  onClose: () => void;
}

function renderCopay(refill: Refill): string {
  if (refill.copayAmt < 0 || refill.unableToCalculate) return 'Unavailable';
  return `$${refill.copayAmt.toFixed(2)}`;
}

function renderStatus(raw: Record<string, unknown>): string {
  const status = String(raw.RefillStatus || '');
  return status.indexOf('After') === -1 ? status : `Refill ${status}`;
}

export function ConfirmOrderPanel({ view, customerServicePhone, onClose }: ConfirmOrderPanelProps) {
  const {
    selectedRefills,
    confirmUserName,
    deliveryMethod,
    countPrice,
    error,
    loading,
    submitSuccess,
    linkToken,
    submitConfirmOrder,
    resetOrder,
    loadRefills,
  } = useRefillsStore();
  const [nyDisclaimer, setNyDisclaimer] = useState(false);
  const isImpersonating = getIsImpersonating();

  const hasInvalidCopay = selectedRefills.some(r => r.unableToCalculate || r.copayAmt < 0);
  const validCopay = !hasInvalidCopay;

  const deliveryMessage =
    deliveryMethod?.DeliveryTypeId === 10001
      ? ' via home delivery'
      : deliveryMethod?.DeliveryTypeId === 10002
        ? ' for pickup'
        : '';

  const handleSubmit = async () => {
    await submitConfirmOrder();
    // On success, submitSuccess becomes true and the inline success banner
    // is shown. The panel stays open until the user acknowledges via Close.
    // On error, the error banner is shown and the panel remains open.
  };

  // Mirrors the legacy modal close + getRefills() sequence:
  // legacy called $modalInstance.close() then getRefills() in the .then() callback.
  // Here we fire loadRefills after the user acknowledges success and we close.
  const handleSuccessClose = () => {
    resetOrder();
    if (linkToken) {
      void loadRefills(linkToken);
    }
    onClose();
  };

  // Any dismissal after a successful submit must run the success cleanup
  // (reset order state + reload refills); pre-submit dismissals just close.
  const handleClose = () => {
    if (submitSuccess) {
      resetOrder();
      if (linkToken) void loadRefills(linkToken);
    }
    onClose();
  };

  // Mirror legacy AngularJS behavior: confirm order opens as a UI Bootstrap
  // modal of size "xlg" (see refillController.openConfirmOrder). We render a
  // Bootstrap 3 modal markup portaled to document.body so it overlays the
  // entire page rather than rendering inline within the refills panel.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.classList.add('modal-open');
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.classList.remove('modal-open');
      document.body.style.overflow = previous;
    };
  }, []);

  const body = (
    <div className="row">
      <div className="col-lg-12 col-md-12 col-sm-12 mobile-confirm-review">
        <h2>
          Review Order <small style={{ float: 'right' }}>{confirmUserName}</small>
        </h2>
        <hr />
      </div>

      <div
        className="col-lg-12 col-md-12 col-sm-12 mobile-confirm-instructions"
        style={{ textAlign: 'center', borderTop: '1px gainsboro solid' }}
      >
        <h3>
          This order contains <span className="highlight"> {selectedRefills.length}</span>{' '}
          prescription(s) for {confirmUserName}
          <span className="highlight">{deliveryMessage}</span>
        </h3>

        {view === 'List' && (
          <div className="table-responsive">
            <table className="table table-bordered table-striped">
              <thead>
                <tr>
                  <th>Rx#</th>
                  <th>Name</th>
                  <th>Quantity</th>
                  <th>Days Supply</th>
                  <th>Co-pay*</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {selectedRefills.map(refill => (
                  <tr key={refill.rx}>
                    <td>{refill.rx}</td>
                    <td>
                      {refill.name}
                      <br />
                      <em>
                        <small className="refill-message-confirm">
                          {String(refill.raw.RefillMessage || '')}
                        </small>
                      </em>
                    </td>
                    <td>{formatNumber(Number(refill.raw.WrittenQty || 0))}</td>
                    <td>{formatNumber(Number(refill.raw.DaysSupply || 0))}</td>
                    <td>{renderCopay(refill)}</td>
                    <td>
                      <button type="button" className={`btn btn-sm ${refill.refillClass}`}>
                        {renderStatus(refill.raw)}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {view === 'Grid' && (
          <div className="panel-body pre-scrollable" style={{ maxHeight: 380 }}>
            <div className="row" style={{ padding: 5 }}>
              {selectedRefills.map(refill => (
                <div
                  key={refill.rx}
                  className="col-md-3 financial-parent"
                  style={{ height: 180, padding: 5 }}
                >
                  <table className="table-bordered grid-confirm-order">
                    <tbody>
                      <tr>
                        <td className="table-row-header">
                          <strong>Rx#: {refill.rx}</strong>
                        </td>
                      </tr>
                      <tr>
                        <td>Name:{refill.name}</td>
                      </tr>
                      <tr>
                        <td>Quantity: {formatNumber(Number(refill.raw.WrittenQty || 0))}</td>
                      </tr>
                      <tr>
                        <td>Days Supply: {formatNumber(Number(refill.raw.DaysSupply || 0))}</td>
                      </tr>
                      <tr>
                        <td>{renderCopay(refill)}</td>
                      </tr>
                      <tr>
                        <td>
                          <button type="button" className={`btn btn-sm ${refill.refillClass}`}>
                            {renderStatus(refill.raw)}
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="copay">
          *Co-pays are estimated based on previous orders.
          <br />
          If no previous order exists, the co-pay estimate is unavailable.
          <br />
          The actual co-payment amount will be determined when the order is processed.
        </p>
        <hr />

        {loading && (
          <div className="text-center" style={{ padding: 20 }}>
            <i className="fa fa-spinner fa-spin" /> Submitting Order...
          </div>
        )}

        {/* Success banner — shown in place of alertify.alert in the legacy flow.
            The legacy displayed a blocking alertify popup then closed the modal.
            Here the same information is presented inline within the panel so the
            user can read the full order summary before dismissing. */}
        {submitSuccess && (
          <div className="alert alert-success" style={{ marginTop: 16 }}>
            <strong>Submit Order - Success</strong>
            <hr style={{ margin: '8px 0' }} />
            <strong>Thank you for your order.</strong>
            <br />
            An order confirmation has been sent to your email address.
          </div>
        )}
      </div>

      <div className="row">
        <div className="col-lg-12 col-md-12 col-sm-12">
          {validCopay && (
            <p className="text-center copay">Total Co-pay = ${countPrice.toFixed(2)}</p>
          )}
          {!validCopay && (
            <p className="text-center copay">Total Co-pay Is Not Available For This Order.</p>
          )}
          <br />
          <p className="text-center">
            If you have any questions about your co-pay, please contact customer service at{' '}
            {customerServicePhone}.
          </p>
        </div>
      </div>

      <div className="row">
        <div className="col-xs-12 col-md-9 col-md-push-1">
          <div className="form-group checkbox" style={{ margin: '0 20px' }}>
            <label htmlFor="nyCheck-confirm">
              <input
                name="nyCheck"
                id="nyCheck-confirm"
                type="checkbox"
                checked={nyDisclaimer}
                onChange={e => setNyDisclaimer(e.target.checked)}
                style={{ height: 13 }}
              />
              <i>
                By checking this box, you acknowledge the following: Certain states require that we
                advise patients about the risk of paying for medical services with a credit card.
                Medical debts have protections under federal and state laws, specifically the
                prohibitions against wage garnishment, property liens and reporting of medical debt
                to credit bureaus. If you pay medical bills with a credit card, those protections
                may not apply.{' '}
                <b>
                  By choosing to pay with a credit card, you acknowledge that you were notified of
                  the risks, and you agree to proceed with this payment method.
                </b>
              </i>{' '}
              <a
                href="https://vytlone.com/wp-content/uploads/2026/02/FAQs-New-York-State-Notice-for-Patients-Paying-by-Credit-Card.pdf"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="NY credit card patient FAQ"
              >
                <span aria-hidden="true" style={{ fontWeight: 700, fontSize: '1.15em', lineHeight: 1 }}>
                  &#9432;
                </span>
              </a>
            </label>
          </div>
        </div>

        {!submitSuccess && (
          <div className="hidden-xs hidden-sm">
            <div className="col-md-1 col-md-pull-9">
              <button type="button" className="btn btn-danger btn-lg" onClick={onClose}>
                Cancel
              </button>
            </div>
            <div className="col-md-2">
              <button
                type="button"
                disabled={loading || !nyDisclaimer || isImpersonating}
                className="btn btn-success btn-lg pull-right"
                onClick={handleSubmit}
              >
                Submit Now
              </button>
            </div>
          </div>
        )}

        {submitSuccess && (
          <div className="hidden-xs hidden-sm">
            <div className="col-md-2 col-md-push-10">
              <button
                type="button"
                className="btn btn-primary btn-lg pull-right"
                onClick={handleClose}
              >
                Close
              </button>
            </div>
          </div>
        )}

        {!submitSuccess && (
          <div className="hidden-md hidden-lg">
            <div className="col-xs-12">
              <button
                type="button"
                disabled={loading || !nyDisclaimer || isImpersonating}
                className="btn btn-success btn-lg pull-right"
                onClick={handleSubmit}
              >
                Submit Now
              </button>
            </div>
            <div className="col-xs-12">
              <button type="button" className="btn btn-danger btn-lg" onClick={onClose}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {submitSuccess && (
          <div className="hidden-md hidden-lg">
            <div className="col-xs-12">
              <button
                type="button"
                className="btn btn-primary btn-lg pull-right"
                onClick={handleClose}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="col-lg-12 col-md-12 col-sm-12">
          <div className="alert alert-danger" style={{ marginTop: 10 }}>
            {error}
          </div>
        </div>
      )}
    </div>
  );

  const modal = (
    <>
      <div
        className="modal fade in"
        role="dialog"
        tabIndex={-1}
        style={{ display: 'block', overflowY: 'auto' }}
        onClick={e => { if (e.target === e.currentTarget) handleClose(); }}
        onKeyDown={e => { if (e.key === 'Escape') handleClose(); }}
      >
        <div className="modal-dialog modal-xlg">
          <div className="modal-content" style={{ padding: 20 }}>
            {body}
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade in" />
    </>
  );

  if (typeof document === 'undefined') return modal;
  return createPortal(modal, document.body);
}
